import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DataContext = createContext();

// Team members + templates kept for Demo Mode event generation
const teamMembers = [
  { name: 'Sarah Chen', initials: 'SC' },
  { name: 'Mike Torres', initials: 'MT' },
  { name: 'Jennifer Walsh', initials: 'JW' },
  { name: 'Robert Kim', initials: 'RK' },
  { name: 'John Doe', initials: 'JD' },
];

function getAuthHeaders() {
  const token = localStorage.getItem('d3_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export const DataProvider = ({ children }) => {
  const [meetings, setMeetings] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [risks, setRisks] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [processingMeetingId, setProcessingMeetingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [audioProcessingId, setAudioProcessingId] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const pollRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const pingRef = useRef(null);

  // ── Fetch all data on mount ──────────────────────────────

  const fetchAll = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [mRes, tRes, rRes, nRes, aRes, actRes] = await Promise.all([
        fetch(`${API_URL}/api/dashboard/meetings`, { headers }),
        fetch(`${API_URL}/api/dashboard/tasks`, { headers }),
        fetch(`${API_URL}/api/dashboard/risks`, { headers }),
        fetch(`${API_URL}/api/dashboard/notifications`, { headers }),
        fetch(`${API_URL}/api/dashboard/analytics`, { headers }),
        fetch(`${API_URL}/api/dashboard/activity`, { headers }),
      ]);
      if (mRes.ok) setMeetings(await mRes.json());
      if (tRes.ok) setTasks(await tRes.json());
      if (rRes.ok) setRisks(await rRes.json());
      if (nRes.ok) setNotifications(await nRes.json());
      if (aRes.ok) setAnalytics(await aRes.json());
      if (actRes.ok) setRecentActivity(await actRes.json());
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Refresh analytics + activity (called after mutations)
  const refreshStats = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [aRes, actRes] = await Promise.all([
        fetch(`${API_URL}/api/dashboard/analytics`, { headers }),
        fetch(`${API_URL}/api/dashboard/activity`, { headers }),
      ]);
      if (aRes.ok) setAnalytics(await aRes.json());
      if (actRes.ok) setRecentActivity(await actRes.json());
    } catch (err) { /* silent */ }
  }, []);

  // ── WebSocket connection ─────────────────────────────────

  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem('d3_token');
    if (!token) return;

    // Build WS URL from API URL
    const wsProtocol = API_URL.startsWith('https') ? 'wss' : 'ws';
    const wsHost = API_URL.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/api/ws?token=${token}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[WS] Connected');
        setWsConnected(true);
        // Start ping interval
        if (pingRef.current) clearInterval(pingRef.current);
        pingRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'pong') return;

          switch (msg.type) {
            case 'task_created':
              setTasks(prev => {
                if (prev.some(t => t.id === msg.data.id)) return prev;
                return [msg.data, ...prev];
              });
              refreshStats();
              break;
            case 'task_updated':
              setTasks(prev => prev.map(t => t.id === msg.data.id ? { ...t, ...msg.data } : t));
              refreshStats();
              break;
            case 'task_deleted':
              setTasks(prev => prev.filter(t => t.id !== msg.data.id));
              refreshStats();
              break;
            case 'risk_created':
              setRisks(prev => {
                if (prev.some(r => r.id === msg.data.id)) return prev;
                return [msg.data, ...prev];
              });
              refreshStats();
              break;
            case 'risk_updated':
              setRisks(prev => prev.map(r => r.id === msg.data.id ? { ...r, ...msg.data } : r));
              refreshStats();
              break;
            case 'meeting_created':
              setMeetings(prev => {
                if (prev.some(m => m.id === msg.data.id)) return prev;
                return [msg.data, ...prev];
              });
              break;
            case 'meeting_processed':
              setMeetings(prev => prev.map(m =>
                m.id === msg.data.meetingId ? { ...m, processed: true, status: 'processed', summary: msg.data.summary } : m
              ));
              // Refresh tasks, risks, notifications after meeting processing
              (async () => {
                const headers = getAuthHeaders();
                const [tRes, rRes, nRes] = await Promise.all([
                  fetch(`${API_URL}/api/dashboard/tasks`, { headers }),
                  fetch(`${API_URL}/api/dashboard/risks`, { headers }),
                  fetch(`${API_URL}/api/dashboard/notifications`, { headers }),
                ]);
                if (tRes.ok) setTasks(await tRes.json());
                if (rRes.ok) setRisks(await rRes.json());
                if (nRes.ok) setNotifications(await nRes.json());
                refreshStats();
              })();
              break;
            case 'notification_read':
              setNotifications(prev => prev.map(n => n.id === msg.data.id ? { ...n, read: true } : n));
              break;
            case 'notifications_read_all':
              setNotifications(prev => prev.map(n => ({ ...n, read: true })));
              break;
            default:
              break;
          }
        } catch (err) {
          console.error('[WS] Parse error:', err);
        }
      };

      ws.onclose = (e) => {
        console.log('[WS] Disconnected:', e.code);
        setWsConnected(false);
        if (pingRef.current) clearInterval(pingRef.current);
        // Reconnect after 3 seconds (unless intentional close)
        if (e.code !== 1000 && e.code < 4000) {
          reconnectRef.current = setTimeout(connectWebSocket, 3000);
        }
      };

      ws.onerror = () => {
        console.error('[WS] Error');
        ws.close();
      };
    } catch (err) {
      console.error('[WS] Connection failed:', err);
      reconnectRef.current = setTimeout(connectWebSocket, 5000);
    }
  }, [refreshStats]);

  useEffect(() => {
    // Connect WS after initial data load
    const timer = setTimeout(connectWebSocket, 1000);
    return () => {
      clearTimeout(timer);
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (pingRef.current) clearInterval(pingRef.current);
      if (wsRef.current) {
        wsRef.current.close(1000);
        wsRef.current = null;
      }
    };
  }, [connectWebSocket]);

  // ── Computed stats from analytics endpoint ───────────────

  const getStats = useCallback(() => {
    if (analytics) return analytics;
    const totalTasks = tasks.length;
    const completedTasks = tasks.filter(t => t.column === 'completed').length;
    return {
      totalTasks,
      completedTasks,
      overdueTasks: tasks.filter(t => t.column !== 'completed').length,
      openRisks: risks.filter(r => r.status !== 'resolved').length,
      processedMeetings: meetings.filter(m => m.processed).length,
      completionRate: totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0,
      highRisks: risks.filter(r => r.severity === 'high' && r.status !== 'resolved').length,
      totalMeetings: meetings.length,
      aiTasks: tasks.filter(t => t.meetingId).length,
    };
  }, [analytics, tasks, risks, meetings]);

  const getTasksForMeeting = useCallback((meetingId) => {
    return tasks.filter(t => t.meetingId === meetingId);
  }, [tasks]);

  const getRisksForMeeting = useCallback((meetingId) => {
    return risks.filter(r => r.meetingId === meetingId);
  }, [risks]);

  const getRecentActivity = useCallback(() => {
    return recentActivity;
  }, [recentActivity]);

  // ── Meeting actions ──────────────────────────────────────

  const processMeeting = useCallback(async (meetingId, transcript) => {
    const meeting = meetings.find(m => m.id === meetingId);
    if (!meeting) return;

    setProcessingMeetingId(meetingId);

    try {
      const body = transcript ? { transcript } : {};
      const res = await fetch(`${API_URL}/api/dashboard/meetings/${meetingId}/process`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });

      if (res.ok) {
        const data = await res.json();
        setMeetings(prev => prev.map(m =>
          m.id === meetingId ? { ...m, processed: true, status: 'processed', summary: data.summary, processedAt: new Date().toISOString() } : m
        ));
        setTasks(prev => [...prev, ...data.tasks]);
        setRisks(prev => [...prev, ...data.risks]);
        const headers = getAuthHeaders();
        const nRes = await fetch(`${API_URL}/api/dashboard/notifications`, { headers });
        if (nRes.ok) setNotifications(await nRes.json());
        refreshStats();
      }
    } catch (err) {
      console.error('Failed to process meeting:', err);
    }

    setProcessingMeetingId(null);
  }, [meetings, refreshStats]);

  // ── Audio upload + pipeline ──────────────────────────────

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPolling = useCallback((meetingId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/api/dashboard/meetings/${meetingId}/status`, {
          headers: getAuthHeaders(),
        });
        if (!res.ok) return;
        const data = await res.json();

        setMeetings(prev => prev.map(m =>
          m.id === meetingId ? {
            ...m,
            status: data.status,
            processed: data.processed,
            summary: data.summary || m.summary,
            decisions: data.decisions || m.decisions,
            audioFile: data.audioFile || m.audioFile,
            transcriptMeta: data.transcriptMeta || m.transcriptMeta,
            aiAnalysis: data.aiAnalysis || m.aiAnalysis,
            processedAt: data.processedAt || m.processedAt,
            processingError: data.processingError,
          } : m
        ));

        if (["processed", "failed", "transcript_ready"].includes(data.status)) {
          stopPolling();
          setAudioProcessingId(null);
          const headers = getAuthHeaders();
          const [tRes, rRes, nRes] = await Promise.all([
            fetch(`${API_URL}/api/dashboard/tasks`, { headers }),
            fetch(`${API_URL}/api/dashboard/risks`, { headers }),
            fetch(`${API_URL}/api/dashboard/notifications`, { headers }),
          ]);
          if (tRes.ok) setTasks(await tRes.json());
          if (rRes.ok) setRisks(await rRes.json());
          if (nRes.ok) setNotifications(await nRes.json());
          refreshStats();
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 2000);
  }, [stopPolling, refreshStats]);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const uploadMeetingAudio = useCallback(async (meetingId, file) => {
    setAudioProcessingId(meetingId);
    setMeetings(prev => prev.map(m =>
      m.id === meetingId ? { ...m, status: 'uploading', processingError: null } : m
    ));

    try {
      const token = localStorage.getItem('d3_token');
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API_URL}/api/dashboard/meetings/${meetingId}/upload-audio`, {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json();
        setMeetings(prev => prev.map(m =>
          m.id === meetingId ? { ...m, status: 'failed', processingError: err.detail || 'Upload failed' } : m
        ));
        setAudioProcessingId(null);
        return { error: err.detail || 'Upload failed' };
      }

      const data = await res.json();
      setMeetings(prev => prev.map(m =>
        m.id === meetingId ? { ...m, status: 'uploading', audioFile: data.audioFile } : m
      ));

      startPolling(meetingId);
      return data;
    } catch (err) {
      console.error('Audio upload failed:', err);
      setMeetings(prev => prev.map(m =>
        m.id === meetingId ? { ...m, status: 'failed', processingError: 'Network error during upload' } : m
      ));
      setAudioProcessingId(null);
      return { error: 'Network error' };
    }
  }, [startPolling]);

  const retranscribeMeeting = useCallback(async (meetingId) => {
    setAudioProcessingId(meetingId);
    setMeetings(prev => prev.map(m =>
      m.id === meetingId ? { ...m, status: 'transcribing', processed: false, processingError: null } : m
    ));

    try {
      const res = await fetch(`${API_URL}/api/dashboard/meetings/${meetingId}/retranscribe`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!res.ok) {
        const err = await res.json();
        setMeetings(prev => prev.map(m =>
          m.id === meetingId ? { ...m, status: 'failed', processingError: err.detail || 'Re-transcribe failed' } : m
        ));
        setAudioProcessingId(null);
        return { error: err.detail };
      }

      startPolling(meetingId);
      return await res.json();
    } catch (err) {
      console.error('Retranscribe failed:', err);
      setAudioProcessingId(null);
      return { error: 'Network error' };
    }
  }, [startPolling]);

  // ── Task actions ─────────────────────────────────────────

  const createTask = useCallback(async (taskData) => {
    try {
      const res = await fetch(`${API_URL}/api/dashboard/tasks`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(taskData),
      });
      if (res.ok) {
        const newTask = await res.json();
        setTasks(prev => {
          if (prev.some(t => t.id === newTask.id)) return prev;
          return [newTask, ...prev];
        });
        const nRes = await fetch(`${API_URL}/api/dashboard/notifications`, { headers: getAuthHeaders() });
        if (nRes.ok) setNotifications(await nRes.json());
        refreshStats();
        return newTask;
      }
    } catch (err) {
      console.error('Failed to create task:', err);
    }
  }, [refreshStats]);

  const updateTask = useCallback(async (taskId, updates) => {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...updates } : t));
    try {
      await fetch(`${API_URL}/api/dashboard/tasks/${taskId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates),
      });
      if (updates.column === 'completed') {
        const nRes = await fetch(`${API_URL}/api/dashboard/notifications`, { headers: getAuthHeaders() });
        if (nRes.ok) setNotifications(await nRes.json());
      }
      refreshStats();
    } catch (err) {
      console.error('Failed to update task:', err);
    }
  }, [refreshStats]);

  const deleteTask = useCallback(async (taskId) => {
    setTasks(prev => prev.filter(t => t.id !== taskId));
    try {
      await fetch(`${API_URL}/api/dashboard/tasks/${taskId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      refreshStats();
    } catch (err) {
      console.error('Failed to delete task:', err);
    }
  }, [refreshStats]);

  const moveTask = useCallback((taskId, newColumn) => {
    updateTask(taskId, { column: newColumn });
  }, [updateTask]);

  const reorderTasks = useCallback(async (orderUpdates) => {
    setTasks(prev => {
      const orderMap = Object.fromEntries(orderUpdates.map(u => [u.id, u.order]));
      return prev.map(t => orderMap[t.id] !== undefined ? { ...t, order: orderMap[t.id] } : t);
    });
    try {
      await fetch(`${API_URL}/api/dashboard/tasks/reorder`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ orders: orderUpdates }),
      });
    } catch (err) {
      console.error('Failed to reorder tasks:', err);
    }
  }, []);

  // ── Risk actions ─────────────────────────────────────────

  const createRisk = useCallback(async (riskData) => {
    try {
      const res = await fetch(`${API_URL}/api/dashboard/risks`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(riskData),
      });
      if (res.ok) {
        const newRisk = await res.json();
        setRisks(prev => {
          if (prev.some(r => r.id === newRisk.id)) return prev;
          return [newRisk, ...prev];
        });
        const nRes = await fetch(`${API_URL}/api/dashboard/notifications`, { headers: getAuthHeaders() });
        if (nRes.ok) setNotifications(await nRes.json());
        refreshStats();
        return newRisk;
      }
    } catch (err) {
      console.error('Failed to create risk:', err);
    }
  }, [refreshStats]);

  const updateRisk = useCallback(async (riskId, updates) => {
    setRisks(prev => prev.map(r => r.id === riskId ? { ...r, ...updates } : r));
    try {
      await fetch(`${API_URL}/api/dashboard/risks/${riskId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates),
      });
      if (updates.status === 'resolved') {
        const nRes = await fetch(`${API_URL}/api/dashboard/notifications`, { headers: getAuthHeaders() });
        if (nRes.ok) setNotifications(await nRes.json());
      }
      refreshStats();
    } catch (err) {
      console.error('Failed to update risk:', err);
    }
  }, [refreshStats]);

  // ── Notification actions ─────────────────────────────────

  const addNotification = useCallback((type, message) => {
    const newNotif = { id: String(Date.now()), type, message, time: new Date().toISOString(), read: false };
    setNotifications(prev => [newNotif, ...prev]);
  }, []);

  const markNotificationRead = useCallback(async (id) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    try {
      await fetch(`${API_URL}/api/dashboard/notifications/${id}/read`, {
        method: 'PUT',
        headers: getAuthHeaders(),
      });
    } catch (err) { /* silent */ }
  }, []);

  const markAllNotificationsRead = useCallback(async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    try {
      await fetch(`${API_URL}/api/dashboard/notifications/read-all`, {
        method: 'PUT',
        headers: getAuthHeaders(),
      });
    } catch (err) { /* silent */ }
  }, []);

  // ── Demo Mode ────────────────────────────────────────────

  const [demoMode, setDemoMode] = useState(true);
  const demoTimerRef = useRef(null);

  const toggleDemoMode = useCallback(() => {
    setDemoMode(prev => !prev);
  }, []);

  const demoTaskNames = [
    'Finalize vendor security assessment',
    'Update quarterly compliance report',
    'Review API rate-limiting strategy',
    'Prepare executive briefing deck',
    'Schedule cross-team sync',
    'Audit data retention policies',
    'Complete penetration test summary',
    'Draft incident response playbook',
    'Review cloud cost optimization plan',
    'Update employee onboarding checklist',
  ];

  const demoRiskNames = [
    'Potential data residency compliance gap',
    'Third-party API deprecation risk',
    'Staff turnover in security team',
    'Encryption key rotation overdue',
    'Vendor SLA breach risk detected',
  ];

  useEffect(() => {
    if (!demoMode) {
      if (demoTimerRef.current) clearTimeout(demoTimerRef.current);
      return;
    }

    const runDemoEvent = async () => {
      const eventType = Math.floor(Math.random() * 5);
      const member = teamMembers[Math.floor(Math.random() * teamMembers.length)];

      switch (eventType) {
        case 0: {
          const processedMeetings = meetings.filter(m => m.processed);
          if (processedMeetings.length === 0) break;
          const meeting = processedMeetings[Math.floor(Math.random() * processedMeetings.length)];
          const title = demoTaskNames[Math.floor(Math.random() * demoTaskNames.length)];
          const daysOut = Math.floor(Math.random() * 10) + 3;
          const due = new Date(); due.setDate(due.getDate() + daysOut);
          await createTask({
            title, priority: ['high', 'medium', 'low'][Math.floor(Math.random() * 3)],
            owner: member.name, ownerInitials: member.initials,
            dueDate: due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            meetingId: meeting.id,
          });
          break;
        }
        case 1: {
          const active = tasks.filter(t => t.column !== 'completed');
          if (active.length === 0) break;
          const task = active[Math.floor(Math.random() * active.length)];
          await updateTask(task.id, { column: 'completed' });
          break;
        }
        case 2: {
          const title = demoRiskNames[Math.floor(Math.random() * demoRiskNames.length)];
          const categories = ['Security', 'Compliance', 'Financial', 'Operational'];
          await createRisk({
            title, severity: Math.random() > 0.5 ? 'high' : 'medium',
            owner: member.name,
            category: categories[Math.floor(Math.random() * categories.length)],
            description: `Auto-detected: ${title}. Requires review and mitigation.`,
            mitigation: 'Mitigation plan pending assignment.',
          });
          break;
        }
        case 3: {
          const pending = meetings.filter(m => !m.processed);
          if (pending.length === 0) break;
          const meeting = pending[Math.floor(Math.random() * pending.length)];
          await processMeeting(meeting.id);
          break;
        }
        case 4: {
          const openRisks = risks.filter(r => r.status === 'open' || r.status === 'mitigating');
          if (openRisks.length === 0) break;
          const risk = openRisks[Math.floor(Math.random() * openRisks.length)];
          const nextStatus = risk.status === 'open' ? 'mitigating' : 'resolved';
          await updateRisk(risk.id, { status: nextStatus });
          break;
        }
        default: break;
      }
    };

    const scheduleNext = () => {
      const delay = 20000 + Math.floor(Math.random() * 10000);
      demoTimerRef.current = setTimeout(() => {
        runDemoEvent();
        scheduleNext();
      }, delay);
    };
    scheduleNext();

    return () => {
      if (demoTimerRef.current) clearTimeout(demoTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoMode, meetings, tasks, risks]);

  const value = {
    meetings, tasks, risks, notifications,
    processingMeetingId, loading,
    audioProcessingId, wsConnected,
    demoMode, toggleDemoMode, fetchAll,
    getStats, getTasksForMeeting, getRisksForMeeting, getRecentActivity,
    processMeeting, uploadMeetingAudio, retranscribeMeeting,
    createTask, updateTask, deleteTask, moveTask, reorderTasks,
    createRisk, updateRisk,
    addNotification, markNotificationRead, markAllNotificationsRead,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) throw new Error('useData must be used within a DataProvider');
  return context;
};
